/*
 * STEP 1 — About the Person Being Honored
 * Design: Quiet Reverence — Playfair Display titles, DM Sans body
 * Large, accessible inputs. Tier selector as visual cards.
 */

import { DeceasedInfo, STYLE_PRESET_INFO, TIER_INFO, TONE_INFO, StylePreset, VideoTier, VideoTone } from "./intake";
import { Check } from "lucide-react";

interface Props {
  data: DeceasedInfo;
  onChange: (data: DeceasedInfo) => void;
}

// ── Date Picker Helpers ───────────────────────────────────────────────────────
// Parses a YYYY-MM-DD string into parts, returns empty strings if blank.
function parseDateParts(iso: string): { month: string; day: string; year: string } {
  if (!iso || iso.length < 10) return { month: '', day: '', year: '' };
  const [y, m, d] = iso.split('-');
  return { month: m ?? '', day: d ?? '', year: y ?? '' };
}
// Assembles parts back to YYYY-MM-DD. Returns '' if any part is missing.
function buildIso(month: string, day: string, year: string): string {
  if (!month || !day || !year) return '';
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}
const MONTHS = [
  { v: '01', l: 'January' }, { v: '02', l: 'February' }, { v: '03', l: 'March' },
  { v: '04', l: 'April' },   { v: '05', l: 'May' },       { v: '06', l: 'June' },
  { v: '07', l: 'July' },    { v: '08', l: 'August' },    { v: '09', l: 'September' },
  { v: '10', l: 'October' }, { v: '11', l: 'November' },  { v: '12', l: 'December' },
];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1899 }, (_, i) => String(currentYear - i));

interface DatePickerProps {
  id: string;
  value: string; // YYYY-MM-DD or ''
  onChange: (iso: string) => void;
}
function DatePicker({ id, value, onChange }: DatePickerProps) {
  const { month, day, year } = parseDateParts(value);
  const selectClass = "flex-1 px-2 py-3 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition appearance-none";
  const update = (m: string, d: string, y: string) => onChange(buildIso(m, d, y));
  return (
    <div className="flex gap-2" id={id}>
      <select
        aria-label="Month"
        value={month}
        onChange={e => update(e.target.value, day, year)}
        className={selectClass}
      >
        <option value="">Month</option>
        {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
      </select>
      <select
        aria-label="Day"
        value={day}
        onChange={e => update(month, e.target.value, year)}
        className={`${selectClass} w-20 flex-none`}
      >
        <option value="">Day</option>
        {DAYS.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
      </select>
      <select
        aria-label="Year"
        value={year}
        onChange={e => update(month, day, e.target.value)}
        className={`${selectClass} w-28 flex-none`}
      >
        <option value="">Year</option>
        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function Step1Info({ data, onChange }: Props) {
  const set = (field: keyof DeceasedInfo, value: string) =>
    onChange({ ...data, [field]: value });

  return (
    <div className="step-enter space-y-8">
      {/* Name */}
      <div className="space-y-4">
        <h3 className="font-['Playfair_Display'] text-lg font-600 text-foreground/70 tracking-wide uppercase text-sm">
          In Loving Memory of
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label htmlFor="deceased-first-name" className="text-sm font-medium text-foreground/70">First Name</label>
            <input
              id="deceased-first-name"
              name="deceased_first_name"
              type="text"
              autoComplete="off"
              value={data.firstName}
              onChange={e => set('firstName', e.target.value)}
              placeholder="First name"
              className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="deceased-last-name" className="text-sm font-medium text-foreground/70">Last Name</label>
            <input
              id="deceased-last-name"
              name="deceased_last_name"
              type="text"
              autoComplete="off"
              value={data.lastName}
              onChange={e => set('lastName', e.target.value)}
              placeholder="Last name"
              className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
            />
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground/70">Date of Birth</label>
          <DatePicker
            id="date-of-birth"
            value={data.dateOfBirth}
            onChange={v => set('dateOfBirth', v)}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground/70">Date of Passing</label>
          <DatePicker
            id="date-of-passing"
            value={data.dateOfPassing}
            onChange={v => set('dateOfPassing', v)}
          />
        </div>
      </div>

      {/* Ornamental divider */}
      <div className="ornament-divider">
        <span className="text-xs font-['Playfair_Display'] italic">Video Style</span>
      </div>

      {/* Tier Selection */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground/70">Choose Your Package</p>
        <div className="space-y-3" role="radiogroup" aria-label="Video package tier">
          {(Object.entries(TIER_INFO) as [VideoTier, typeof TIER_INFO[VideoTier]][]).map(([key, info]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={data.tier === key}
              onClick={() => set('tier', key)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                data.tier === key
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    data.tier === key ? 'border-primary bg-primary' : 'border-border'
                  }`}>
                    {data.tier === key && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground font-['Playfair_Display']">{info.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{info.description}</div>
                  </div>
                </div>
                <span className="font-['Playfair_Display'] font-700 text-lg text-primary ml-4 flex-shrink-0">{info.price}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tone Selection */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground/70">Video Tone</p>
        <div className="grid grid-cols-1 gap-2" role="radiogroup" aria-label="Video tone">
          {(Object.entries(TONE_INFO) as [VideoTone, typeof TONE_INFO[VideoTone]][]).map(([key, info]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={data.tone === key}
              onClick={() => set('tone', key)}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${
                data.tone === key
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/40'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                data.tone === key ? 'border-primary bg-primary' : 'border-border'
              }`}>
                {data.tone === key && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
              </div>
              <div>
                <span className="font-medium text-foreground text-sm">{info.label}</span>
                <span className="text-muted-foreground text-xs ml-2">— {info.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Memorial Style Picker */}
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground/70">Memorial Style</p>
          <p className="text-xs text-muted-foreground">Choose the visual style for your intro and outro title cards. Leave on Auto to let us match it to your tone.</p>
        </div>
        {/* Auto option */}
        <button
          type="button"
          role="radio"
          aria-checked={data.stylePreset === 'auto'}
          onClick={() => set('stylePreset', 'auto')}
          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${
            data.stylePreset === 'auto'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-card hover:border-primary/40'
          }`}
        >
          <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            data.stylePreset === 'auto' ? 'border-primary bg-primary' : 'border-border'
          }`}>
            {data.stylePreset === 'auto' && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
          </div>
          <div>
            <span className="font-medium text-foreground text-sm">Auto-Select</span>
            <span className="text-muted-foreground text-xs ml-2">— We choose the best style based on your tone and music</span>
          </div>
        </button>
        {/* Style thumbnail grid */}
        <div className="grid grid-cols-2 gap-2">
          {(Object.entries(STYLE_PRESET_INFO) as [StylePreset, typeof STYLE_PRESET_INFO[StylePreset]][]).filter(([k]) => k !== 'auto').map(([key, info]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={data.stylePreset === key}
              onClick={() => set('stylePreset', key)}
              className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 text-left ${
                data.stylePreset === key
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'border-border hover:border-primary/40'
              }`}
            >
              {/* Thumbnail */}
              <div className="relative h-20 bg-muted">
                {info.thumbnailUrl && (
                  <img
                    src={info.thumbnailUrl}
                    alt={info.label}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/30 pointer-events-none" />
                {/* Selected checkmark */}
                {data.stylePreset === key && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
                {/* Accent color dot */}
                <div
                  className="absolute bottom-1.5 left-1.5 w-3 h-3 rounded-full border border-white/50"
                  style={{ backgroundColor: info.accentHex }}
                />
              </div>
              {/* Label */}
              <div className="px-2 py-1.5 bg-card">
                <p className="text-xs font-semibold text-foreground leading-tight">{info.label}</p>
                <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 line-clamp-1">{info.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tagline */}
      <div className="space-y-1.5">
        <label htmlFor="tagline" className="text-sm font-medium text-foreground/70">
          Tagline <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <input
          id="tagline"
          name="tagline"
          type="text"
          autoComplete="off"
          value={data.tagline}
          onChange={e => set('tagline', e.target.value)}
          placeholder="e.g. Beloved Father, Husband & Friend"
          maxLength={80}
          className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
        />
        <p className="text-xs text-muted-foreground">Appears on the intro title card beneath the name and dates.</p>
      </div>

      {/* Ornamental divider */}
      <div className="ornament-divider">
        <span className="text-xs font-['Playfair_Display'] italic">Your Contact Info</span>
      </div>

      {/* Contact */}
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground/70">Your Name</label>
          <input
            id="contact-name"
            name="contact_name"
            type="text"
            autoComplete="name"
            value={data.primaryContactName}
            onChange={e => set('primaryContactName', e.target.value)}
            placeholder="Your full name"
            className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground/70">Email Address</label>
          <input
            id="contact-email"
            name="contact_email"
            type="email"
            autoComplete="email"
            value={data.primaryContactEmail}
            onChange={e => set('primaryContactEmail', e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
          />
          <p className="text-xs text-muted-foreground">We'll send your preview and final video to this address.</p>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="contact-phone" className="text-sm font-medium text-foreground/70">Phone Number</label>
          <input
            id="contact-phone"
            name="contact_phone"
            type="tel"
            autoComplete="tel"
            value={data.primaryContactPhone}
            onChange={e => set('primaryContactPhone', e.target.value)}
            placeholder="(555) 000-0000"
            className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
          />
        </div>
      </div>
    </div>
  );
}
